const YOUTUBE_VIDEO_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/;
const VIMEO_VIDEO_ID_REGEX = /^\d+$/;
const VIMEO_HASH_REGEX = /^[a-zA-Z0-9]+$/;

function isValidYouTubeId(videoId: string | null | undefined): videoId is string {
  return !!videoId && YOUTUBE_VIDEO_ID_REGEX.test(videoId);
}

function isValidVimeoId(videoId: string | null | undefined): videoId is string {
  return !!videoId && VIMEO_VIDEO_ID_REGEX.test(videoId);
}

function getPathSegment(pathname: string, index: number): string | null {
  const segments = pathname.split('/').filter(Boolean);
  return segments[index] ?? null;
}

function buildYouTubeEmbedUrl(parsedUrl: URL, hostname: string): string | null {
  if (hostname === 'youtu.be') {
    const videoId = getPathSegment(parsedUrl.pathname, 0);
    return isValidYouTubeId(videoId) ? `https://www.youtube.com/embed/${videoId}` : null;
  }

  if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
    if (parsedUrl.pathname === '/watch') {
      const videoId = parsedUrl.searchParams.get('v');
      return isValidYouTubeId(videoId) ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    if (parsedUrl.pathname.startsWith('/embed/')) {
      const videoId = getPathSegment(parsedUrl.pathname, 1);
      return isValidYouTubeId(videoId) ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    if (parsedUrl.pathname.startsWith('/shorts/')) {
      const videoId = getPathSegment(parsedUrl.pathname, 1);
      return isValidYouTubeId(videoId) ? `https://www.youtube.com/embed/${videoId}` : null;
    }
  }

  return null;
}

function vimeoEmbedWithQueryHash(videoId: string, parsedUrl: URL): string {
  const queryHash = parsedUrl.searchParams.get('h');
  if (queryHash && VIMEO_HASH_REGEX.test(queryHash)) {
    return `https://player.vimeo.com/video/${videoId}?h=${queryHash}`;
  }
  return `https://player.vimeo.com/video/${videoId}`;
}

function buildVimeoEmbedUrl(parsedUrl: URL, hostname: string): string | null {
  if (hostname === 'player.vimeo.com') {
    if (parsedUrl.pathname.startsWith('/video/')) {
      const videoId = getPathSegment(parsedUrl.pathname, 1);
      return isValidVimeoId(videoId) ? vimeoEmbedWithQueryHash(videoId, parsedUrl) : null;
    }
    return null;
  }

  if (hostname !== 'vimeo.com') return null;

  const segments = parsedUrl.pathname.split('/').filter(Boolean);

  if (segments[0] === 'channels' && segments.length >= 3) {
    const videoId = segments[2];
    return isValidVimeoId(videoId) ? vimeoEmbedWithQueryHash(videoId, parsedUrl) : null;
  }

  const videoId = segments[0];
  if (!isValidVimeoId(videoId)) return null;

  const pathHash = segments[1];
  if (pathHash !== undefined) {
    return VIMEO_HASH_REGEX.test(pathHash) ? `https://player.vimeo.com/video/${videoId}?h=${pathHash}` : null;
  }

  return vimeoEmbedWithQueryHash(videoId, parsedUrl);
}

export function getVideoEmbedUrl(url: string): string | null {
  if (!url.trim()) return null;

  try {
    const parsedUrl = new URL(url.trim());
    const hostname = parsedUrl.hostname.replace(/^www\./, '').toLowerCase();

    return buildYouTubeEmbedUrl(parsedUrl, hostname) ?? buildVimeoEmbedUrl(parsedUrl, hostname);
  } catch { // eslint-disable-line no-restricted-syntax -- video URL parse fallback
    return null;
  }
}

export function isValidVideoUrl(url: string): boolean {
  return getVideoEmbedUrl(url) !== null;
}
